import { Button, Typography, useMediaQuery } from "@mui/material"
import { useFormik } from "formik"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import LoadingApp from "../../../components/LoadingApp"
import ModalAction from "../../../components/ModalAction"
import ModalFeedBack from "../../../components/ModalFeedBack"
import { ChangePasswordValidation } from "../../../interfaces/validation/ChangePasswordValidation"
import { alterarSenha } from "../../../services/LoginService"
import "./styles.css"
import { sxButton, sxOutlinedInputPasswordChange, sxTypographyTitle } from "./sxProps"
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext"
import PasswordInput from "../../../components/PasswordInput"
import PageTitle from "../../../components/Accessibility/PageTitle"

const AlterarSenha: React.FC = () => {

  const accessibility = useAccessibility();


  const navigate = useNavigate();
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const [loading, setLoading] = useState<boolean>(false);
  const [lengthValid, setLengthValid] = useState(false);
  const [uppercaseValid, setUppercaseValid] = useState(false);
  const [lowercaseValid, setLowercaseValid] = useState(false);
  const [numberValid, setNumberValid] = useState(false);
  const [specialCharValid, setSpecialCharValid] = useState(false);
  const [validNewPass, setValidNewPass] = useState(false);
  const [currentPassDoesntMatch, setCurrentPassDoesntMatch] = useState<boolean>(false);
  const currentPassDoesntMatchMessage = 'Senha atual não confere';

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void; textOutlinedButton: string; textContainedButton: string;
    title: string; subTitle?: string; handleOutlinedButton?: () => void; handleContainedButton?: () => void;
  }>(null);

  const formik = useFormik({
    initialValues: {
      password: '',
      newPassword: '',
      newPasswordConfirmation: ''
    },
    validationSchema: ChangePasswordValidation,
    onSubmit: () => {
      openModalConfirmPasswordChange('Atualizar senha', 'Tem certeza que deseja alterar a sua senha?');
    },
    validateOnChange: false
  });

  const openModalConfirmPasswordChange = (title: string, subtitle: string) => {
    setModalAction({
      title: title,
      subTitle: subtitle,
      textOutlinedButton: 'Cancelar',
      textContainedButton: 'Alterar',
      handleContainedButton: handleConfirmPasswordChange,
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction
    })
  }

  const handleCloseModalAction = () => {
    setModalAction(null);
  }

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const openModalSuccess = () => {
    setModalFeedback({
      icon: 'success',
      title: 'Senha atualizada',
      subTitle: 'Sua senha foi alterada com sucesso!',
      textButton: 'Voltar ao início',
      closeAction: closeModalSuccess,
      buttonAction: closeModalSuccess,
      showButtonModal: true
    });
  }

  const closeModalSuccess = async () => {
    setModalFeedback(null);
    navigate('/home');
  }

  const handleFieldChange = (fieldName: string, value: string) => {
    formik.setFieldValue(fieldName, value.trim());

    // Mínimo de 10 caracteres
    let lengthValid = value.length >= 10;
    setLengthValid(lengthValid);

    // Pelo menos uma letra maiúscula
    let uppercaseValid = /[A-Z]/.test(value);
    setUppercaseValid(uppercaseValid);

    // Pelo menos uma letra minúscula
    let lowerCaseValid = /[a-z]/.test(value);
    setLowercaseValid(lowerCaseValid);

    // Pelo menos um número
    let numberValid = /\d/.test(value)
    setNumberValid(numberValid);

    // Pelo menos um caractere especial
    let specialCharValid = /[@#$&]/.test(value)
    setSpecialCharValid(specialCharValid);

    if (lengthValid && uppercaseValid && lowerCaseValid && numberValid && specialCharValid) {
      setValidNewPass(true)
    } else {
      setValidNewPass(false)
    }
  };

  const handleConfirmPasswordChange = async () => {
    handleCloseModalAction();
    setLoading(true);
    const senha = formik.values.password;
    const novaSenha = formik.values.newPassword;
    const novaSenhaConfirmacao = formik.values.newPasswordConfirmation;
    const result = await alterarSenha(senha, novaSenha, novaSenhaConfirmacao);
    if (result?.status === 200) {
      openModalSuccess();
    } else {
      openModalError('Atenção', !!result?.data.mensagem ? result.data.mensagem : 'Erro ao alterar senha. Tente novamente em alguns instantes.');
      const currentPassDoesntMatch = result?.data.mensagem === currentPassDoesntMatchMessage;
      setCurrentPassDoesntMatch(currentPassDoesntMatch);
    }
    setLoading(false);
  }

  return (
    <>
      {loading ? <LoadingApp showLoading={loading} /> :
        <div style={accessibility.textTitleStyleAccessibility} className="change-password-container">
          <PageTitle text="Redefinir senha" marginBottom="16px" />
          <span style={accessibility.textStyleAccessibility} className='login-text login-text-center'>
            Cadastre uma nova senha de acesso.
          </span>
          <form onSubmit={formik.handleSubmit} className='form-change-password'>
            <div>
              <PasswordInput
                id='senhaAtual'
                label="Senha atual"
                placeholder="Digite sua senha"
                name="password"
                textError={formik.errors.password}
                error={!!formik.errors.password || currentPassDoesntMatch}
                maxLength={30}
                onChange={formik.handleChange}
                fullWidth={mobileScreen}
                sx={sxOutlinedInputPasswordChange}
              />
            </div>
            <div>
              <PasswordInput
                id='novaSenha'
                label="Nova senha"
                placeholder="Digite sua nova senha"
                name='newPassword'
                textError={formik.errors.newPassword || formik.errors.newPasswordConfirmation}
                error={!!formik.errors.newPassword || !!formik.errors.newPasswordConfirmation}
                maxLength={30}
                onChange={e => handleFieldChange('newPassword', e.target.value)}
                value={formik.values.newPassword}
                fullWidth={mobileScreen}
                sx={sxOutlinedInputPasswordChange}
              />
            </div>
            <div>
              <PasswordInput
                id='confirmarSenha'
                label="Confirmar nova senha"
                placeholder='Confirmar nova senha'
                name='newPasswordConfirmation'
                textError={formik.errors.newPasswordConfirmation}
                error={!!formik.errors.newPasswordConfirmation}
                maxLength={30}
                onChange={formik.handleChange}
                value={formik.values.newPasswordConfirmation}
                fullWidth={mobileScreen}
                sx={sxOutlinedInputPasswordChange}
              />
            </div>
            <div className="password-infos-container" style={{ ...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility }}>
              <Typography style={accessibility.textStyleAccessibility}>Por questões de segurança, a sua senha deve seguir os seguintes critérios:</Typography>
              <Typography style={accessibility.textStyleAccessibility} sx={lengthValid ? { color: '#156D44' } : { color: '#CC3D3D' }}><span aria-hidden="true">{lengthValid ? '\u2713' : 'X'}</span> Mínimo de 10 caracteres</Typography>
              <Typography style={accessibility.textStyleAccessibility} sx={uppercaseValid ? { color: '#156D44' } : { color: '#CC3D3D' }}><span aria-hidden="true">{uppercaseValid ? '\u2713' : 'X'}</span> Pelo menos uma letra maiúscula</Typography>
              <Typography style={accessibility.textStyleAccessibility} sx={lowercaseValid ? { color: '#156D44' } : { color: '#CC3D3D' }}><span aria-hidden="true">{lowercaseValid ? '\u2713' : 'X'}</span> Pelo menos uma letra minúscula</Typography>
              <Typography style={accessibility.textStyleAccessibility} sx={numberValid ? { color: '#156D44' } : { color: '#CC3D3D' }}><span aria-hidden="true">{numberValid ? '\u2713' : 'X'}</span> Pelo menos um número</Typography>
              <Typography style={accessibility.textStyleAccessibility} sx={specialCharValid ? { color: '#156D44' } : { color: '#CC3D3D' }}><span aria-hidden="true">{specialCharValid ? '\u2713' : 'X'}</span> Pelo menos um caractere especial (Ex: # @ $ &)</Typography>
            </div>
            <Button
              disabled={!validNewPass || !formik.values.password || !formik.values.newPasswordConfirmation}
              type='submit'
              sx={sxButton} variant='contained'
              style={accessibility.buttonStyleAccessibility}
            >
              <Typography style={accessibility.buttonTextStyleAccessibility} fontFamily={'UnimedSans-Bold'}>ATUALIZAR SENHA</Typography>
            </Button>
          </form>
        </div>
      }
      <ModalFeedBack
        showModal={!!modalFeedback}
        icon={modalFeedback?.icon as 'success' | 'error'}
        textButton={modalFeedback?.textButton as string}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        showButtonModal={modalFeedback?.showButtonModal}
        handleShowModal={modalFeedback?.closeAction}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        textOutlinedButton={modalAction?.textOutlinedButton || ''}
        textContainedButton={modalAction?.textContainedButton || ''}
        title={modalAction?.title || ''}
        subTitle={modalAction?.subTitle || ''}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
    </>
  )
}

export default AlterarSenha;